/**
 * TS-5.12 / TS-5.13 EXTEND — billable + customer fields on time_entries.
 *
 * Sibling to migration 00260 (added `is_billable boolean` + `customer_id
 * text` to public.timesheet_time_entries). Drives the consumption side:
 * how to set the billable flag, how to detect mismatches, how to roll up
 * billable hours per customer, and how contract type → billable behavior.
 *
 *   classifyContractBillability — given a contract type (T&M / FFP /
 *   CPFF / IDIQ / FFP_LOE / etc.), return whether work on this contract
 *   is normally billable, costed-only, or non-billable
 *
 *   resolveBillableFlag — given (chargeCodeCategory, contract,
 *   workerType), return the canonical is_billable value the entry
 *   surface should set when the worker picks the code
 *
 *   detectBillableMismatch — given a set of entries + their
 *   contracts, surface entries where stored is_billable disagrees
 *   with what the contract type implies
 *
 *   summarizeBillableByCustomer — per-customer rollup of billable
 *   hours + dollars + entry count + worker count + last entry date
 *
 *   computeUtilizationRate — given worker entries over a period,
 *   return billable / total ratio + classification tier (excellent /
 *   target / below_target / poor)
 *
 * Composes with TS-5.12/5.13 migration 00260 (the columns this helper
 * consumes), TS-5.14 notes (already on table), TS-13.19 customer
 * invoice prep (billable + customer drive invoice rollup), TS-13.18
 * pay stub viewer (utilization rate surfaces on worker dashboard),
 * TS-14.4 audit_chain (mismatches log billable.mismatch entries).
 */

// ============================================================
// Contract type → billable behavior
// ============================================================

export type ContractType =
  | 'time_and_materials' // T&M — billable at actual hours × rate
  | 'firm_fixed_price' // FFP — costed but not billed per hour
  | 'cost_plus_fixed_fee' // CPFF — billable at cost + fixed fee allocation
  | 'cost_plus_award_fee' // CPAF — similar to CPFF
  | 'idiq' // IDIQ — task-order-dependent (usually inherits parent)
  | 'ffp_loe' // FFP-LOE — fixed price for level of effort
  | 'internal' // internal cost objective — never billable
  | 'unknown'

export type ContractBillabilityBehavior =
  | 'billable_at_actuals'
  | 'costed_not_billed'
  | 'never_billable'
  | 'task_order_dependent'

export interface ContractBillabilityProfile {
  contractType: ContractType
  behavior: ContractBillabilityBehavior
  defaultIsBillable: boolean
  reason: string
}

/**
 * Map contract type to its canonical billability behavior. Pure.
 *
 * Behavior taxonomy (FAR / audit / industry standard):
 *   - billable_at_actuals: each labor hour bills to the customer at
 *     the contracted rate (T&M, CPFF, CPAF)
 *   - costed_not_billed: labor flows into cost objective but the
 *     customer pays a fixed total, not per-hour (FFP, FFP-LOE)
 *   - never_billable: internal cost objective; never invoiced
 *   - task_order_dependent: IDIQ — the parent vehicle defines the
 *     ceiling, individual task orders specify the actual type
 *
 * defaultIsBillable: what should the entry surface set when the
 * worker picks this contract's charge code, BEFORE manual override.
 */
export function classifyContractBillability(args: {
  contractType: ContractType
}): ContractBillabilityProfile {
  switch (args.contractType) {
    case 'time_and_materials':
    case 'cost_plus_fixed_fee':
    case 'cost_plus_award_fee':
      return {
        contractType: args.contractType,
        behavior: 'billable_at_actuals',
        defaultIsBillable: true,
        reason: `${args.contractType} bills at actual hours × negotiated rate.`,
      }
    case 'firm_fixed_price':
    case 'ffp_loe':
      return {
        contractType: args.contractType,
        behavior: 'costed_not_billed',
        defaultIsBillable: false,
        reason: `${args.contractType} bills total fixed price; per-hour entry is costed but not invoiced.`,
      }
    case 'internal':
      return {
        contractType: 'internal',
        behavior: 'never_billable',
        defaultIsBillable: false,
        reason: 'Internal cost objective — never invoiced to a customer.',
      }
    case 'idiq':
      return {
        contractType: 'idiq',
        behavior: 'task_order_dependent',
        defaultIsBillable: false,
        reason: 'IDIQ parent — billability defined by underlying task order.',
      }
    case 'unknown':
    default:
      return {
        contractType: 'unknown',
        behavior: 'never_billable',
        defaultIsBillable: false,
        reason: 'Unknown contract type — defaults to non-billable for safety.',
      }
  }
}

// ============================================================
// Billable flag resolver
// ============================================================

export type ChargeCodeCategory =
  | 'direct_project'
  | 'b_and_p'
  | 'ird'
  | 'overhead'
  | 'g_and_a'
  | 'fringe'
  | 'pto'
  | 'sick'
  | 'holiday'
  | 'training'
  | 'meeting'
  | 'indirect'

export interface BillableFlagResolution {
  isBillable: boolean
  customerId: string | null
  reason: string
}

/**
 * Given charge-code category + contract context, return the canonical
 * is_billable + customer_id the entry surface should set. Pure.
 *
 * Logic:
 *   - PTO / sick / holiday / training / meeting / indirect: never
 *     billable; no customer (these are non-project absences)
 *   - B&P / IR&D / overhead / G&A / fringe: cost-pool work; never
 *     billable to a customer (charged against company indirect pool)
 *   - direct_project + contract.billable_at_actuals: billable, with
 *     contract.customerId
 *   - direct_project + costed_not_billed: false but customer set
 *     (so utilization shows "worked for customer X" even if FFP)
 *   - direct_project + never_billable / task_order_dependent: false,
 *     no customer
 */
export function resolveBillableFlag(args: {
  chargeCodeCategory: ChargeCodeCategory
  contract?: {
    contractType: ContractType
    customerId: string | null
  }
}): BillableFlagResolution {
  // Non-customer-facing categories first
  const NON_BILLABLE_CATEGORIES = new Set<ChargeCodeCategory>([
    'pto',
    'sick',
    'holiday',
    'training',
    'meeting',
    'indirect',
    'b_and_p',
    'ird',
    'overhead',
    'g_and_a',
    'fringe',
  ])
  if (NON_BILLABLE_CATEGORIES.has(args.chargeCodeCategory)) {
    return {
      isBillable: false,
      customerId: null,
      reason: `${args.chargeCodeCategory} is a non-customer-facing category.`,
    }
  }
  // direct_project: depends on contract type
  if (args.chargeCodeCategory === 'direct_project') {
    if (!args.contract) {
      return {
        isBillable: false,
        customerId: null,
        reason: 'direct_project category but no contract supplied.',
      }
    }
    const profile = classifyContractBillability({
      contractType: args.contract.contractType,
    })
    if (profile.behavior === 'billable_at_actuals') {
      return {
        isBillable: true,
        customerId: args.contract.customerId,
        reason: `${args.contract.contractType} contract — bills at actuals.`,
      }
    }
    if (profile.behavior === 'costed_not_billed') {
      return {
        isBillable: false,
        customerId: args.contract.customerId, // surface customer for utilization rollup
        reason: `${args.contract.contractType} — customer tracked for utilization, not billing.`,
      }
    }
    return {
      isBillable: false,
      customerId: null,
      reason: profile.reason,
    }
  }
  return {
    isBillable: false,
    customerId: null,
    reason: 'Unrecognized charge code category.',
  }
}

// ============================================================
// Billable mismatch detector
// ============================================================

export type MismatchKind =
  | 'stored_billable_contract_ffp'
  | 'stored_nonbillable_contract_tm'
  | 'missing_customer_on_billable'
  | 'customer_without_billable_flag'

export interface TimeEntryForBillability {
  id: string
  date: string
  workerId: string
  chargeCodeCategory: ChargeCodeCategory
  contractId: string | null
  isBillable: boolean
  customerId: string | null
  hours: number
}

export interface ContractRef {
  contractId: string
  contractType: ContractType
  customerId: string | null
}

export interface MismatchRow {
  entryId: string
  kind: MismatchKind
  storedIsBillable: boolean
  storedCustomerId: string | null
  contractType: ContractType
  expectedIsBillable: boolean
  expectedCustomerId: string | null
  reason: string
}

/**
 * Surface entries where stored is_billable disagrees with what the
 * contract type implies. Pure.
 *
 * Mismatch kinds:
 *   - stored_billable_contract_ffp: entry.is_billable=true but
 *     contract is FFP/FFP-LOE/internal/unknown
 *   - stored_nonbillable_contract_tm: entry.is_billable=false but
 *     contract is T&M/CPFF/CPAF
 *   - missing_customer_on_billable: is_billable=true but customer_id
 *     is null (would block invoice prep)
 *   - customer_without_billable_flag: customer_id set but
 *     is_billable=false AND contract is billable_at_actuals (likely
 *     forgot to flip the flag)
 *
 * Returns one row per mismatch (an entry can surface multiple kinds
 * — e.g. billable=true on FFP AND missing customer). Sorted by date
 * desc (newest issues first).
 */
export function detectBillableMismatch(args: {
  entries: TimeEntryForBillability[]
  contractsById: Map<string, ContractRef>
}): MismatchRow[] {
  const out: MismatchRow[] = []
  for (const entry of args.entries) {
    if (!entry.contractId) continue
    const contract = args.contractsById.get(entry.contractId)
    if (!contract) continue
    const expected = resolveBillableFlag({
      chargeCodeCategory: entry.chargeCodeCategory,
      contract: {
        contractType: contract.contractType,
        customerId: contract.customerId,
      },
    })
    // Mismatch 1: stored billable but contract isn't
    if (entry.isBillable && !expected.isBillable) {
      out.push({
        entryId: entry.id,
        kind: 'stored_billable_contract_ffp',
        storedIsBillable: entry.isBillable,
        storedCustomerId: entry.customerId,
        contractType: contract.contractType,
        expectedIsBillable: expected.isBillable,
        expectedCustomerId: expected.customerId,
        reason: `Entry marked billable but ${contract.contractType} contract is non-billable.`,
      })
    }
    // Mismatch 2: stored non-billable but contract IS billable
    if (!entry.isBillable && expected.isBillable) {
      out.push({
        entryId: entry.id,
        kind: 'stored_nonbillable_contract_tm',
        storedIsBillable: entry.isBillable,
        storedCustomerId: entry.customerId,
        contractType: contract.contractType,
        expectedIsBillable: expected.isBillable,
        expectedCustomerId: expected.customerId,
        reason: `Entry marked non-billable but ${contract.contractType} contract is billable at actuals.`,
      })
    }
    // Mismatch 3: billable but no customer (blocks invoice prep)
    if (entry.isBillable && !entry.customerId) {
      out.push({
        entryId: entry.id,
        kind: 'missing_customer_on_billable',
        storedIsBillable: entry.isBillable,
        storedCustomerId: entry.customerId,
        contractType: contract.contractType,
        expectedIsBillable: expected.isBillable,
        expectedCustomerId: expected.customerId,
        reason: 'is_billable=true but customer_id is null — invoice prep will skip.',
      })
    }
    // Mismatch 4: customer set + contract billable but flag not set
    if (
      !entry.isBillable &&
      entry.customerId &&
      expected.isBillable &&
      contract.customerId === entry.customerId
    ) {
      out.push({
        entryId: entry.id,
        kind: 'customer_without_billable_flag',
        storedIsBillable: entry.isBillable,
        storedCustomerId: entry.customerId,
        contractType: contract.contractType,
        expectedIsBillable: expected.isBillable,
        expectedCustomerId: expected.customerId,
        reason: `Customer ${entry.customerId} matches billable contract but is_billable=false.`,
      })
    }
  }
  // Sort newest-issue-first by joining with entry date
  const entryDateMap = new Map(args.entries.map((e) => [e.id, e.date]))
  return out.sort((a, b) => {
    const da = entryDateMap.get(a.entryId) ?? ''
    const db = entryDateMap.get(b.entryId) ?? ''
    return db.localeCompare(da)
  })
}

// ============================================================
// Per-customer billable rollup
// ============================================================

export interface CustomerBillableRow {
  customerId: string
  billableHours: number
  nonBillableHours: number
  totalHours: number
  utilizationRate: number
  distinctEntryCount: number
  distinctWorkerCount: number
  firstEntryDate: string
  lastEntryDate: string
}

/**
 * Per-customer rollup of hours + utilization. Pure.
 *
 * utilizationRate = billableHours / totalHours within this customer's
 * entries (caller can request fleet-wide via computeUtilizationRate
 * instead).
 *
 * Excludes entries with null customer_id (can't roll them up by
 * customer). Sorted by billableHours desc.
 */
export function summarizeBillableByCustomer(args: {
  entries: TimeEntryForBillability[]
}): CustomerBillableRow[] {
  const byCustomer = new Map<
    string,
    {
      billable: number
      nonBillable: number
      entries: Set<string>
      workers: Set<string>
      first: string
      last: string
    }
  >()
  for (const e of args.entries) {
    if (!e.customerId) continue
    const slot = byCustomer.get(e.customerId) ?? {
      billable: 0,
      nonBillable: 0,
      entries: new Set<string>(),
      workers: new Set<string>(),
      first: e.date,
      last: e.date,
    }
    if (e.isBillable) slot.billable += e.hours
    else slot.nonBillable += e.hours
    slot.entries.add(e.id)
    slot.workers.add(e.workerId)
    if (e.date < slot.first) slot.first = e.date
    if (e.date > slot.last) slot.last = e.date
    byCustomer.set(e.customerId, slot)
  }
  const out: CustomerBillableRow[] = []
  for (const [customerId, slot] of byCustomer) {
    const total = slot.billable + slot.nonBillable
    out.push({
      customerId,
      billableHours: Number(slot.billable.toFixed(2)),
      nonBillableHours: Number(slot.nonBillable.toFixed(2)),
      totalHours: Number(total.toFixed(2)),
      utilizationRate: total > 0 ? Number((slot.billable / total).toFixed(4)) : 0,
      distinctEntryCount: slot.entries.size,
      distinctWorkerCount: slot.workers.size,
      firstEntryDate: slot.first,
      lastEntryDate: slot.last,
    })
  }
  return out.sort((a, b) => b.billableHours - a.billableHours)
}

// ============================================================
// Utilization rate computer
// ============================================================

export type UtilizationTier = 'excellent' | 'target' | 'below_target' | 'poor'

export interface UtilizationResult {
  workerId: string
  billableHours: number
  nonBillableHours: number
  totalHours: number
  utilizationRate: number
  tier: UtilizationTier
  reason: string
}

/**
 * Per-worker utilization rate + tier classification. Pure.
 *
 * tier rules (default thresholds, caller can override):
 *   - excellent: ≥ 0.85 (highly billable, common in consulting)
 *   - target: 0.65 ≤ rate < 0.85 (industry standard for billable
 *     professionals)
 *   - below_target: 0.40 ≤ rate < 0.65
 *   - poor: < 0.40
 *
 * Caller passes only the worker's entries — filtering by date range
 * happens upstream.
 */
export function computeUtilizationRate(args: {
  workerId: string
  entries: TimeEntryForBillability[]
  excellentThreshold?: number
  targetThreshold?: number
  belowTargetThreshold?: number
}): UtilizationResult {
  const excellentThreshold = args.excellentThreshold ?? 0.85
  const targetThreshold = args.targetThreshold ?? 0.65
  const belowTargetThreshold = args.belowTargetThreshold ?? 0.4
  const scoped = args.entries.filter((e) => e.workerId === args.workerId)
  let billable = 0
  let nonBillable = 0
  for (const e of scoped) {
    if (e.isBillable) billable += e.hours
    else nonBillable += e.hours
  }
  const total = billable + nonBillable
  const rate = total > 0 ? billable / total : 0
  let tier: UtilizationTier
  if (rate >= excellentThreshold) tier = 'excellent'
  else if (rate >= targetThreshold) tier = 'target'
  else if (rate >= belowTargetThreshold) tier = 'below_target'
  else tier = 'poor'
  return {
    workerId: args.workerId,
    billableHours: Number(billable.toFixed(2)),
    nonBillableHours: Number(nonBillable.toFixed(2)),
    totalHours: Number(total.toFixed(2)),
    utilizationRate: Number(rate.toFixed(4)),
    tier,
    reason:
      total === 0
        ? 'No entries for worker in period.'
        : `${billable}/${total}h billable (${(rate * 100).toFixed(0)}%) → ${tier}.`,
  }
}
